import React from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Heart, Zap, Moon, BarChart3 } from 'lucide-react';
import { ChartTooltip } from './DashboardShared';

const TrendChartPanel = ({ activeChart, setActiveChart, chartData, aiMsg, taskStats7d, taskStats30d, userState }) => (
  <div className="glass-card glass-card-active relative rounded-[22px] overflow-hidden" style={{ padding: 0 }}>
    {/* Panel glow */}
    <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 10% 5%, rgba(168,85,247,0.12) 0%, transparent 45%)' }} />
    <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 90% 95%, rgba(245,158,11,0.08) 0%, transparent 40%)' }} />

    {/* ── TREND CHART ── */}
    <div className="relative z-10 p-5 pb-4" data-testid="chart-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
            background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.2)',
            boxShadow: '0 0 10px rgba(168,85,247,0.1)',
          }}>
            <TrendingUp className="w-4 h-4" style={{ color: '#A855F7' }} />
          </div>
          <h3 style={{ color: '#F8FAFC', fontWeight: 700, fontSize: '18px', letterSpacing: '-0.01em' }}>Son 14 Günlük Trend</h3>
        </div>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 mb-5">
        {[
          { key: 'mood', label: 'Ruh Hali', icon: Heart, color: '#C084FC', bg: 'rgba(168,85,247,0.15)', glow: 'rgba(168,85,247,0.35)' },
          { key: 'focus', label: 'Enerji', icon: Zap, color: '#FCD34D', bg: 'rgba(245,158,11,0.15)', glow: 'rgba(245,158,11,0.35)' },
          { key: 'sleep', label: 'Uyku', icon: Moon, color: '#67E8F9', bg: 'rgba(56,189,248,0.15)', glow: 'rgba(56,189,248,0.35)' },
        ].map(f => (
          <button key={f.key} onClick={() => setActiveChart(f.key)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-semibold transition-all duration-200"
            style={{
              background: f.bg,
              border: `1px solid ${activeChart === f.key ? f.glow : 'transparent'}`,
              color: f.color,
              boxShadow: activeChart === f.key ? `0 0 14px ${f.glow}, inset 0 0 8px ${f.glow}40` : `0 0 8px ${f.glow}30`,
              transform: activeChart === f.key ? 'scale(1.05)' : 'scale(1)',
            }} data-testid={`chart-filter-${f.key}`}>
            <f.icon className="w-3.5 h-3.5" />
            {f.label}
          </button>
        ))}
      </div>

      {/* Multi-line chart */}
      {chartData.length > 0 ? (
        <div className="h-44 rounded-xl p-2" style={{ background: 'linear-gradient(180deg, rgba(20,30,60,0.4), rgba(10,20,40,0.6))' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
              <defs>
                <linearGradient id="gradMood" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A855F7" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#A855F7" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradSleep" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.10} />
                  <stop offset="95%" stopColor="#38BDF8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradFocus" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.10} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 10 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 10 }} />
              <Tooltip content={<ChartTooltip />} />
              {/* Render inactive lines first, active line LAST so it's always visible on top */}
              {activeChart !== 'sleep' && (
                <Area type="monotone" dataKey="Uyku" stroke="#38BDF8" strokeWidth={1.5} fill="url(#gradSleep)" strokeOpacity={0.4}
                  dot={{ fill: '#38BDF8', strokeWidth: 0, r: 2 }}
                  activeDot={{ fill: '#38BDF8', strokeWidth: 0, r: 3.5, style: { filter: 'drop-shadow(0 0 6px rgba(56,189,248,0.7))' } }} />
              )}
              {activeChart !== 'focus' && (
                <Area type="monotone" dataKey="Odak" stroke="#F59E0B" strokeWidth={1.5} fill="url(#gradFocus)" strokeOpacity={0.4}
                  dot={{ fill: '#F59E0B', strokeWidth: 0, r: 2 }}
                  activeDot={{ fill: '#F59E0B', strokeWidth: 0, r: 3.5, style: { filter: 'drop-shadow(0 0 6px rgba(245,158,11,0.7))' } }} />
              )}
              {activeChart !== 'mood' && (
                <Area type="monotone" dataKey="Ruh Hali" stroke="#A855F7" strokeWidth={1.5} fill="url(#gradMood)" strokeOpacity={0.4}
                  dot={{ fill: '#A855F7', strokeWidth: 0, r: 2 }}
                  activeDot={{ fill: '#A855F7', strokeWidth: 0, r: 3.5, style: { filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.7))' } }} />
              )}
              {/* Active line rendered LAST = on top */}
              {activeChart === 'mood' && (
                <Area type="monotone" dataKey="Ruh Hali" stroke="#A855F7" strokeWidth={2.5} fill="url(#gradMood)"
                  dot={{ fill: '#A855F7', strokeWidth: 0, r: 3 }}
                  activeDot={{ fill: '#A855F7', strokeWidth: 0, r: 5, style: { filter: 'drop-shadow(0 0 8px rgba(168,85,247,0.8))' } }}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(168,85,247,0.5))' }} />
              )}
              {activeChart === 'sleep' && (
                <Area type="monotone" dataKey="Uyku" stroke="#38BDF8" strokeWidth={2.5} fill="url(#gradSleep)"
                  dot={{ fill: '#38BDF8', strokeWidth: 0, r: 3 }}
                  activeDot={{ fill: '#38BDF8', strokeWidth: 0, r: 5, style: { filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.8))' } }}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.5))' }} />
              )}
              {activeChart === 'focus' && (
                <Area type="monotone" dataKey="Odak" stroke="#F59E0B" strokeWidth={2.5} fill="url(#gradFocus)"
                  dot={{ fill: '#F59E0B', strokeWidth: 0, r: 3 }}
                  activeDot={{ fill: '#F59E0B', strokeWidth: 0, r: 5, style: { filter: 'drop-shadow(0 0 8px rgba(245,158,11,0.8))' } }}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(245,158,11,0.5))' }} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-44 flex items-center justify-center rounded-xl" style={{ background: 'rgba(10,20,40,0.4)' }}>
          <p className="text-xs" style={{ color: '#475569' }}>Henuz veri yok</p>
        </div>
      )}

      {/* AI trend insight */}
      {aiMsg.trend_story && (
        <div className="mt-3 text-glass-panel" style={{
          background: 'linear-gradient(180deg, rgba(168,85,247,0.04), rgba(255,255,255,0.01))',
          border: '1px solid rgba(168,85,247,0.1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), inset 0 0 15px rgba(168,85,247,0.03)',
        }}>
          <p className="leading-[1.7]" style={{ color: '#D6DCE8', fontSize: '13px' }} data-testid="trend-story-text">{aiMsg.trend_story}</p>
          <p className="mt-1.5" style={{ color: '#64748B', fontSize: '11px' }}>Günlük check-in bilgilerine göre</p>
        </div>
      )}
    </div>

    {/* Separator */}
    <div className="h-[1px] mx-5" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)' }} />

    {/* ── GÖREV TAKİBİ ── */}
    <div className="relative z-10 p-5 pt-4" data-testid="task-tracking-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
          background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)',
          boxShadow: '0 0 10px rgba(56,189,248,0.08)',
        }}>
          <BarChart3 className="w-4 h-4" style={{ color: '#38BDF8' }} />
        </div>
        <h3 style={{ color: '#F8FAFC', fontWeight: 700, fontSize: '18px', letterSpacing: '-0.01em' }}>Görev Takibi</h3>
      </div>

      {/* 3 stat boxes */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="relative rounded-xl p-3 text-center overflow-hidden" style={{
          background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.12)',
          boxShadow: 'inset 0 0 20px rgba(245,158,11,0.04)',
        }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(245,158,11,0.08), transparent 70%)' }} />
          <p className="relative text-2xl font-bold" style={{ color: '#FBBF24' }} data-testid="stat-7d-num">{taskStats7d.completed || 0}</p>
          <p className="relative text-[10px] font-medium mt-0.5" style={{ color: '#CBD5E1' }}>Son 7 Gün</p>
          <p className="relative text-[9px] mt-0.5" style={{ color: '#94A3B8' }}>{taskStats7d.completed || 0} görev</p>
        </div>
        <div className="relative rounded-xl p-3 text-center overflow-hidden" style={{
          background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.1)',
          boxShadow: 'inset 0 0 20px rgba(56,189,248,0.04)',
        }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(56,189,248,0.08), transparent 70%)' }} />
          <p className="relative text-2xl font-bold" style={{ color: '#7DD3FC' }}>{taskStats30d.total ? Math.round((taskStats30d.completed / taskStats30d.total) * 100) : 0}%</p>
          <p className="relative text-[10px] font-medium mt-0.5" style={{ color: '#CBD5E1' }}>Son 30 Gün</p>
          <p className="relative text-[9px] mt-0.5" style={{ color: '#94A3B8' }}>{taskStats30d.total || 0} görevden</p>
        </div>
        <div className="relative rounded-xl p-3 text-center overflow-hidden" style={{
          background: 'rgba(168,85,247,0.04)', border: '1px solid rgba(168,85,247,0.1)',
          boxShadow: 'inset 0 0 20px rgba(168,85,247,0.04)',
        }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(168,85,247,0.08), transparent 70%)' }} />
          <p className="relative text-2xl font-bold" style={{ color: '#C084FC' }}>{userState?.streak || 0}</p>
          <p className="relative text-[10px] font-medium mt-0.5" style={{ color: '#CBD5E1' }}>Ardışık Gün</p>
          <p className="relative text-[9px] mt-0.5" style={{ color: '#94A3B8' }}>{userState?.streak || 0} gün</p>
        </div>
      </div>

      {/* Mini task trend */}
      <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-medium" style={{ color: '#94A3B8' }}>Görev tamamlama trendi</span>
          <span className="text-[10px] font-bold" style={{ color: '#F59E0B' }}>{taskStats30d.total ? Math.round((taskStats30d.completed / taskStats30d.total) * 100) : 0}%</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{
            width: `${taskStats30d.total ? Math.round((taskStats30d.completed / taskStats30d.total) * 100) : 0}%`,
            background: 'linear-gradient(90deg, #F59E0B, #FBBF24)',
            boxShadow: '0 0 8px rgba(245,158,11,0.5)',
          }} />
        </div>
      </div>
    </div>

    {/* Bottom LED */}
    <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{
      background: 'linear-gradient(90deg, transparent, rgba(56,189,248,0.3), rgba(168,85,247,0.2), transparent)',
      boxShadow: '0 0 6px rgba(56,189,248,0.2)',
    }} />
  </div>
);

export default TrendChartPanel;
